/**
 * Effets de combat : faisceaux, lueurs d'impact, explosions (cœur, onde de choc, étincelles, fumée, explosions
 * secondaires), annonces de tir. Pool plafonné (comme ASpaceCombatVfx) ; un effet refusé incrémente `dropped`.
 *
 * Les lueurs sont des billboards additifs à bords fondus (et non des sphères pleines) ; les faisceaux ont un cœur
 * et un halo ; les lumières d'explosion restent toujours actives (intensité nulle au repos) pour faire partie des
 * lumières que les modèles reçoivent dès leur chargement.
 */
import {
  Color3,
  Color4,
  Constants,
  DynamicTexture,
  Mesh,
  MeshBuilder,
  ParticleSystem,
  PointLight,
  Quaternion,
  StandardMaterial,
  Vector3,
  type Scene,
} from '@babylonjs/core';

type RGB = readonly [number, number, number];

interface Effect {
  mesh: Mesh;
  kind: 'beam' | 'glow' | 'ring';
  age: number;
  duration: number;
  startScale: number;
  endScale: number;
  peak: number;
  /** Annonce qui suit un vaisseau : décalage appliqué à chaque image. */
  follow?: { shipId: number; anchor: Vector3; base: Vector3 } | null;
  pulse: boolean;
  /** Halo d'un faisceau : suit la même courbe d'opacité, plus faible. */
  halo?: boolean;
}

const MAX_EFFECTS = 128;
const MAX_LIGHTS = 4;
const MAX_BURSTS = 14;
const MAX_SPARKS = 10;
const MAX_SMOKE = 6;
/** Lumières au repos : hors de la scène, intensité nulle (elles restent actives, voir plus haut). */
const PARKED = new Vector3(0, -1e6, 0);

export class Vfx {
  private readonly active: Effect[] = [];
  private readonly free: Record<Effect['kind'], Mesh[]> = { beam: [], glow: [], ring: [] };
  private readonly materials = new Map<string, StandardMaterial>();
  private readonly lights: { light: PointLight; age: number; duration: number; peak: number }[] = [];
  private readonly bursts: ParticleSystem[] = [];
  private readonly sparkSystems: ParticleSystem[] = [];
  private readonly smokeSystems: ParticleSystem[] = [];
  private nextBurst = 0;
  private nextSpark = 0;
  private nextSmoke = 0;
  private readonly flare: DynamicTexture;
  private readonly ringTexture: DynamicTexture;
  private readonly puff: DynamicTexture;
  /** Effets différés (explosions secondaires). */
  private readonly pending: { at: number; run: () => void }[] = [];
  private time = 0;
  dropped = 0;
  reducedFlashes = false;
  particleScale = 1;

  constructor(private readonly scene: Scene) {
    this.flare = makeRadialTexture(scene, 'vfx-flare', [[0, 1], [0.2, 0.85], [0.5, 0.3], [1, 0]]);
    this.ringTexture = makeRadialTexture(scene, 'vfx-ring', [[0, 0], [0.6, 0], [0.82, 1], [0.9, 0.6], [1, 0]]);
    // Fumée : bords très fondus, pas de cœur marqué.
    this.puff = makeRadialTexture(scene, 'vfx-puff', [[0, 0.55], [0.4, 0.35], [0.75, 0.1], [1, 0]]);
    for (let i = 0; i < MAX_LIGHTS; i++) {
      const light = new PointLight(`vfx-light-${i}`, PARKED.clone(), scene);
      light.intensity = 0;
      light.range = 120;
      this.lights.push({ light, age: 1, duration: 0, peak: 0 });
    }
    for (let i = 0; i < MAX_BURSTS; i++) this.bursts.push(this.makeSystem(`vfx-burst-${i}`, 160, ParticleSystem.BLENDMODE_ADD));
    for (let i = 0; i < MAX_SPARKS; i++) {
      const ps = this.makeSystem(`vfx-sparks-${i}`, 120, ParticleSystem.BLENDMODE_ADD);
      // Étincelles étirées le long de leur vitesse.
      ps.billboardMode = ParticleSystem.BILLBOARDMODE_STRETCHED;
      this.sparkSystems.push(ps);
    }
    for (let i = 0; i < MAX_SMOKE; i++) {
      const ps = this.makeSystem(`vfx-smoke-${i}`, 60, ParticleSystem.BLENDMODE_STANDARD);
      ps.particleTexture = this.puff;
      ps.addSizeGradient(0, 0.5);
      ps.addSizeGradient(1, 2.2);
      ps.minAngularSpeed = -0.6;
      ps.maxAngularSpeed = 0.6;
      this.smokeSystems.push(ps);
    }
  }

  private makeSystem(name: string, capacity: number, blendMode: number): ParticleSystem {
    const ps = new ParticleSystem(name, capacity, this.scene);
    ps.particleTexture = this.flare;
    ps.blendMode = blendMode;
    ps.emitter = Vector3.Zero();
    ps.createSphereEmitter(0.5, 1);
    ps.emitRate = 0;
    ps.updateSpeed = 1 / 60;
    ps.disposeOnStop = false;
    return ps;
  }

  /** Matériau émissif ; `additive` : billboard à bords fondus (texture radiale) en mélange additif. */
  material(color: RGB, additive = false, texture: DynamicTexture | null = null): StandardMaterial {
    const key = `${color.map((c) => c.toFixed(3)).join(',')}:${additive ? 'add' : 'solid'}:${texture?.name ?? ''}`;
    let mat = this.materials.get(key);
    if (!mat) {
      mat = new StandardMaterial(`vfx-${key}`, this.scene);
      mat.disableLighting = true;
      mat.emissiveColor = new Color3(color[0], color[1], color[2]);
      mat.diffuseColor = Color3.Black();
      mat.specularColor = Color3.Black();
      mat.backFaceCulling = false;
      if (additive) {
        mat.alphaMode = Constants.ALPHA_ADD;
        mat.disableDepthWrite = true;
        if (texture) mat.opacityTexture = texture;
      }
      this.materials.set(key, mat);
    }
    return mat;
  }

  private take(kind: Effect['kind']): Mesh | null {
    if (this.active.length >= MAX_EFFECTS) {
      this.dropped++;
      return null;
    }
    let mesh = this.free[kind].pop();
    if (!mesh) {
      mesh = kind === 'beam'
        ? MeshBuilder.CreateCylinder('vfx-beam', { height: 1, diameter: 1, tessellation: 8 }, this.scene)
        : MeshBuilder.CreatePlane(`vfx-${kind}`, { size: 1 }, this.scene);
      if (kind !== 'beam') mesh.billboardMode = Mesh.BILLBOARDMODE_ALL;
    }
    mesh.isPickable = false;
    mesh.rotationQuaternion ??= new Quaternion();
    mesh.setEnabled(true);
    return mesh;
  }

  /** Faisceau de `from` à `to` (cœur + halo additif), largeur en mètres, fondu sur `duration`. */
  beam(from: Vector3, to: Vector3, color: RGB, width: number, duration: number, options: { pulse?: boolean; follow?: Effect['follow'] } = {}): void {
    const peak = this.reducedFlashes ? 0.55 : 1;
    const layers: [number, RGB, boolean][] = [
      [width, [Math.min(1, color[0] * 0.5 + 0.5), Math.min(1, color[1] * 0.5 + 0.5), Math.min(1, color[2] * 0.5 + 0.5)], false],
      [width * 3.2, color, true],
    ];
    for (const [w, c, halo] of layers) {
      const mesh = this.take('beam');
      if (!mesh) return;
      placeBeam(mesh, from, to, w);
      mesh.material = this.material(c, halo);
      mesh.visibility = 1;
      this.active.push({
        mesh,
        kind: 'beam',
        age: 0,
        duration: Math.max(0.02, duration),
        startScale: 1,
        endScale: 1,
        peak: halo ? peak * 0.35 : peak,
        follow: options.follow ?? null,
        pulse: options.pulse ?? false,
        halo,
      });
    }
  }

  /** Lueur à bords fondus qui grossit et s'estompe (impact, cœur d'explosion, flash de bouche). */
  flash(position: Vector3, color: RGB, radius: number, duration: number, grow = 1.6): void {
    const mesh = this.take('glow');
    if (!mesh) return;
    mesh.position.copyFrom(position);
    mesh.material = this.material(color, true, this.flare);
    // La texture radiale s'éteint vers le bord : le billboard est plus grand que le rayon visible.
    const size = radius * 2.6;
    mesh.scaling.setAll(size * 0.5);
    this.active.push({
      mesh,
      kind: 'glow',
      age: 0,
      duration: Math.max(0.03, duration),
      startScale: size * 0.5,
      endScale: size * grow,
      peak: this.reducedFlashes ? 0.5 : 1,
      pulse: false,
    });
  }

  /** Onde de choc : anneau qui s'élargit vite et s'efface. */
  ring(position: Vector3, color: RGB, radius: number, duration: number): void {
    if (this.reducedFlashes) return;
    const mesh = this.take('ring');
    if (!mesh) return;
    mesh.position.copyFrom(position);
    mesh.material = this.material(color, true, this.ringTexture);
    mesh.scaling.setAll(radius * 0.3);
    this.active.push({ mesh, kind: 'ring', age: 0, duration, startScale: radius * 0.3, endScale: radius * 2.2, peak: 0.8, pulse: false });
  }

  /** Gerbe de particules additive (cœur d'explosion). */
  burst(position: Vector3, color: RGB, count: number, power: number, size: number, life: number): void {
    const ps = this.bursts[this.nextBurst]!;
    this.nextBurst = (this.nextBurst + 1) % this.bursts.length;
    this.fire(ps, position, count, power, size, life, color, new Color4(color[0] * 0.3, color[1] * 0.1, 0, 0));
  }

  /**
   * Étincelles étirées, projetées dans un cône autour de `normal` (impacts), ou dans toutes les directions
   * sans normale (débris d'explosion).
   */
  sparks(position: Vector3, color: RGB, count: number, speed: number, life: number, normal: Vector3 | null = null): void {
    const ps = this.sparkSystems[this.nextSpark]!;
    this.nextSpark = (this.nextSpark + 1) % this.sparkSystems.length;
    if (normal) {
      // Directions tirées entre deux bornes autour de la normale : un cône d'environ 60°.
      const n = normal.normalizeToNew();
      ps.createDirectedSphereEmitter(0.2, n.add(new Vector3(-0.6, -0.6, -0.6)), n.add(new Vector3(0.6, 0.6, 0.6)));
    } else {
      ps.createSphereEmitter(0.5, 1);
    }
    this.fire(ps, position, count, speed, 0.18, life, color, new Color4(color[0], color[1] * 0.4, 0, 0));
  }

  /** Fumée qui persiste et s'étale après une explosion. */
  smoke(position: Vector3, count: number, size: number, life: number): void {
    const ps = this.smokeSystems[this.nextSmoke]!;
    this.nextSmoke = (this.nextSmoke + 1) % this.smokeSystems.length;
    this.fire(ps, position, count, size * 0.6, size, life, [0.06, 0.055, 0.05], new Color4(0.02, 0.02, 0.02, 0), 0.45, [0.11, 0.1, 0.09]);
  }

  private fire(ps: ParticleSystem, position: Vector3, count: number, power: number, size: number, life: number, color: RGB, dead: Color4, alpha = 1, color2?: RGB): void {
    ps.stop();
    ps.reset();
    ps.emitter = position.clone();
    ps.color1 = new Color4(color[0], color[1], color[2], alpha);
    const c2 = color2 ?? [Math.min(1, color[0] + 0.3), Math.min(1, color[1] + 0.3), Math.min(1, color[2] + 0.2)];
    ps.color2 = new Color4(c2[0], c2[1], c2[2], alpha);
    ps.colorDead = dead;
    ps.minSize = size * 0.5;
    ps.maxSize = size;
    ps.minEmitPower = power * 0.4;
    ps.maxEmitPower = power;
    ps.minLifeTime = life * 0.5;
    ps.maxLifeTime = life;
    ps.manualEmitCount = Math.max(1, Math.round(count * this.particleScale));
    ps.start();
  }

  /** Lumière ponctuelle brève (explosions) ; coupée en mode flashes réduits. */
  light(position: Vector3, color: RGB, intensity: number, range: number, duration: number): void {
    if (this.reducedFlashes) return;
    const slot = this.lights.reduce((a, b) => (a.age / Math.max(a.duration, 1e-3) >= b.age / Math.max(b.duration, 1e-3) ? a : b));
    slot.light.position.copyFrom(position);
    slot.light.diffuse = new Color3(color[0], color[1], color[2]);
    slot.light.range = range;
    slot.age = 0;
    slot.duration = duration;
    slot.peak = intensity;
  }

  /** Exécute `run` après `delay` secondes (explosions secondaires). */
  later(delay: number, run: () => void): void {
    this.pending.push({ at: this.time + delay, run });
  }

  update(dt: number, shipPosition: (id: number) => Vector3 | null): void {
    this.time += dt;
    for (let i = this.pending.length - 1; i >= 0; i--) {
      if (this.pending[i]!.at <= this.time) this.pending.splice(i, 1)[0]!.run();
    }
    for (let i = this.active.length - 1; i >= 0; i--) {
      const e = this.active[i]!;
      e.age += dt;
      const t = e.age / e.duration;
      if (t >= 1) {
        e.mesh.setEnabled(false);
        this.free[e.kind].push(e.mesh);
        this.active.splice(i, 1);
        continue;
      }
      if (e.follow) {
        const pos = shipPosition(e.follow.shipId);
        if (pos) e.mesh.position.copyFrom(e.follow.base.add(pos.subtract(e.follow.anchor)));
      }
      if (e.kind === 'glow' || e.kind === 'ring') {
        const grow = e.kind === 'ring' ? 1 - (1 - t) * (1 - t) : Math.sqrt(t);
        e.mesh.scaling.setAll(e.startScale + (e.endScale - e.startScale) * grow);
        e.mesh.visibility = e.peak * (1 - t) * (e.kind === 'ring' ? 1 : 1 - t);
      } else {
        e.mesh.visibility = e.pulse ? e.peak * (0.45 + 0.4 * Math.abs(Math.sin(e.age * 14)) + 0.15 * t) : e.peak * (1 - t);
      }
    }
    for (const slot of this.lights) {
      if (slot.age >= slot.duration) continue;
      slot.age += dt;
      const t = slot.age / Math.max(slot.duration, 1e-3);
      if (t >= 1) {
        slot.light.intensity = 0;
        slot.light.position.copyFrom(PARKED);
      } else {
        slot.light.intensity = slot.peak * (1 - t) * (1 - t);
      }
    }
  }

  clear(): void {
    for (const e of this.active) {
      e.mesh.setEnabled(false);
      this.free[e.kind].push(e.mesh);
    }
    this.active.length = 0;
    this.pending.length = 0;
    for (const ps of [...this.bursts, ...this.sparkSystems, ...this.smokeSystems]) ps.stop();
    for (const slot of this.lights) {
      slot.light.intensity = 0;
      slot.light.position.copyFrom(PARKED);
      slot.age = slot.duration;
    }
  }

  get activeCount(): number { return this.active.length; }
}

/** Oriente un cylindre unitaire (axe Y) entre deux points. */
export function placeBeam(mesh: Mesh, from: Vector3, to: Vector3, width: number): void {
  const delta = to.subtract(from);
  const length = Math.max(delta.length(), 1e-3);
  mesh.position.copyFrom(from.add(delta.scale(0.5)));
  mesh.scaling.set(width, length, width);
  const dir = delta.scale(1 / length);
  const up = Vector3.Up();
  const axis = Vector3.Cross(up, dir);
  const dot = Vector3.Dot(up, dir);
  mesh.rotationQuaternion ??= new Quaternion();
  if (axis.lengthSquared() < 1e-10) {
    if (dot > 0) mesh.rotationQuaternion.set(0, 0, 0, 1);
    else mesh.rotationQuaternion.copyFrom(Quaternion.RotationAxis(Vector3.Right(), Math.PI));
  } else {
    mesh.rotationQuaternion.copyFrom(Quaternion.RotationAxis(axis.normalize(), Math.acos(Math.max(-1, Math.min(1, dot)))));
  }
}

/** Texture radiale en niveaux de gris (alpha = luminance) : arrêts [rayon 0..1, intensité]. */
function makeRadialTexture(scene: Scene, name: string, stops: [number, number][]): DynamicTexture {
  const size = 128;
  const tex = new DynamicTexture(name, { width: size, height: size }, scene, true);
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  for (const [at, v] of stops) g.addColorStop(at, `rgba(255,255,255,${v})`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  tex.update();
  tex.hasAlpha = true;
  return tex;
}
